import React, { useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import NotificationToastContainer from './components/NotificationToastContainer';
import PushInit from './components/PushInit';
import CourierTrackingInit from './components/CourierTrackingInit';
import SupportWidget from './components/SupportWidget';
import store from './store';
import { getMe, setInitialized } from './store/authSlice';
import { applyTheme } from './utils/theme';
import { mediaUrl } from './utils/media';
// Login-oqimi (birinchi ko'rinadigan, kichik sahifalar) — tezkor birinchi
// chizish uchun ODATDAGIDEK (eager) import qilinadi.
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import LockScreen from './components/LockScreen';
import UpdatePrompt from './components/UpdatePrompt';
import OfflineBanner from './components/OfflineBanner';
import RequireModule from './components/RequireModule';
import { landingPath } from './utils/landingPath';
// Qolgan barcha sahifalar — har biri faqat HAQIQATAN o'sha yo'lga kirilganda
// yuklanadi (React.lazy), asosiy bundle'ga hech biri kirmaydi. Ayniqsa og'ir
// kutubxonalarni (recharts — SalesAnalyticsPage, html2canvas —
// WarehouseManagerPage) ishlatuvchi sahifalar uchun muhim.
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ProductFormPage = lazy(() => import('./pages/ProductFormPage'));
const PurchaseFormPage = lazy(() => import('./pages/PurchaseFormPage'));
const SalesFormPage = lazy(() => import('./pages/SalesFormPage'));
const SalesAnalyticsPage = lazy(() => import('./pages/SalesAnalyticsPage'));
const SalesAgentDetailPage = lazy(() => import('./pages/SalesAgentDetailPage'));
const VisitsAgentDetailPage = lazy(() => import('./pages/VisitsAgentDetailPage'));
const TaskBoardPage = lazy(() => import('./pages/TaskBoardPage'));
const MyTasksPage = lazy(() => import('./pages/MyTasksPage'));
const MyTaskDetailPage = lazy(() => import('./pages/MyTaskDetailPage'));
const PaymentFormPage = lazy(() => import('./pages/PaymentFormPage'));
const CounterpartyCardPage = lazy(() => import('./pages/CounterpartyCardPage'));
const CounterpartyAgentMatrixPage = lazy(() => import('./pages/CounterpartyAgentMatrixPage'));
const CashboxCardPage = lazy(() => import('./pages/CashboxCardPage'));
const SalesPointCardPage = lazy(() => import('./pages/SalesPointCardPage'));
const TransferFormPage = lazy(() => import('./pages/TransferFormPage'));
const InventarizatsiyaFormPage = lazy(() => import('./pages/InventarizatsiyaFormPage'));
const WriteOffFormPage = lazy(() => import('./pages/WriteOffFormPage'));
const ProductStockCardPage = lazy(() => import('./pages/ProductStockCardPage'));
const ProductionFormPage = lazy(() => import('./pages/ProductionFormPage'));
const ProductionAnalyticsPage = lazy(() => import('./pages/ProductionAnalyticsPage'));
const JobOrderFormPage = lazy(() => import('./pages/JobOrderFormPage'));
const JobOrdersTVPage = lazy(() => import('./pages/JobOrdersTVPage'));
const JobOrdersMapPage = lazy(() => import('./pages/JobOrdersMapPage'));
const ProcurementFormPage = lazy(() => import('./pages/ProcurementFormPage'));
const AktSverkaPublicPage = lazy(() => import('./pages/AktSverkaPublicPage'));
const PriceListPublicPage = lazy(() => import('./pages/PriceListPublicPage'));
const SnabjenistPage = lazy(() => import('./pages/SnabjenistPage'));
const ProcessWorkerPage = lazy(() => import('./pages/ProcessWorkerPage'));
const SalesAgentPage = lazy(() => import('./pages/SalesAgentPage'));
const WarehouseManagerPage = lazy(() => import('./pages/WarehouseManagerPage'));
const CourierPage = lazy(() => import('./pages/CourierPage'));
const DebtCollectorPage = lazy(() => import('./pages/DebtCollectorPage'));
const SmetaPage = lazy(() => import('./pages/SmetaPage'));
const SmetaPublicPage = lazy(() => import('./pages/SmetaPublicPage'));
const SalesInvoicePublicPage = lazy(() => import('./pages/SalesInvoicePublicPage'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));
const BranchReportPage = lazy(() => import('./pages/BranchReportPage'));
const SuperAdminLoginPage = lazy(() => import('./pages/SuperAdminLoginPage'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdminDashboard'));

// /purchases/new va /purchases/:id/edit bitta komponentni ishlatadi — React Router uni
// qayta yaratmay qayta ishlatadi, natijada oldingi hujjat holati (o'chirilmoqda,
// ochiq modal, band qilingan prefill) yangi hujjatga o'tib qoladi. Manzil o'zgarganda
// formani butunlay yangidan yaratish uchun key beriladi.
function PurchaseFormRoute() {
  const loc = useLocation();
  return <PurchaseFormPage key={loc.pathname + loc.search} />;
}

function PrivateRoute({ children }) {
  const { isAuthenticated, initialized } = useSelector((s) => s.auth);
  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  return isAuthenticated ? children : <Navigate to="/login" />;
}

// Sozlamalar kabi sahifalar — owner va admin (keyin qo'shilgan adminlar ham).
// Boshqalar /dashboard'ga yo'naltiriladi.
function AdminRoute({ children }) {
  const { isAuthenticated, initialized, user } = useSelector((s) => s.auth);
  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" />;
  return ['owner', 'admin'].includes(user?.role) ? children : <Navigate to="/m/dashboard" replace />;
}

function PublicRoute({ children }) {
  const { isAuthenticated, initialized, user } = useSelector((s) => s.auth);
  if (!initialized) return null;
  return !isAuthenticated ? children : <Navigate to={landingPath(user)} />;
}

// Root "/" → role-aware landing (snabjenist → /snab, others → /dashboard).
function RootRedirect() {
  const user = useSelector((s) => s.auth.user);
  return <Navigate to={landingPath(user)} replace />;
}

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function AppInit() {
  const dispatch = useDispatch();
  const brandColor = useSelector((s) => s.auth.user?.organization?.brandColor);
  const brandSolid = useSelector((s) => s.auth.user?.organization?.brandSolid);
  const brandBloom = useSelector((s) => s.auth.user?.organization?.brandBloom);
  const orgId = useSelector((s) => s.auth.user?.organization?.id);
  const orgLogo = useSelector((s) => s.auth.user?.organization?.logo);
  const savedIconsRef = useRef(null);   // standart <link> elementlar — logo o'chirilganda tiklash uchun

  // Ilova (ayniqsa mobil PWA) uzoq vaqt fonda turgach, brauzer/OS jarayonni
  // xotiradan chiqarib yuborishi mumkin — qaytadan ochilganda React qaytadan
  // "mount" bo'ladi, va tarmoq hali to'liq tiklanmagan bo'lishi mumkin (10-15
  // daqiqa oflaynlikdan keyin qayta ulanish paytida bir necha soniya kerak
  // bo'ladi). Shu daqiqada getMe() ATIGI BIR MARTA chaqirilib, tarmoq xatosi
  // bilan muvaffaqiyatsiz tugasa — PrivateRoute buni "kirmagan" deb talqin
  // qilib, login sahifasiga chiqarib yuborardi, garchi token hali
  // localStorage'da butunlay yaroqli bo'lsa ham. Shuning uchun bu yerda
  // qayta urinib ko'ramiz (backoff bilan) — faqat HAQIQIY 401'da yoki
  // urinishlar tugagach to'xtaymiz.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      dispatch(setInitialized());
      return;
    }
    let cancelled = false;
    const RETRY_DELAYS_MS = [1000, 2000, 4000, 6000];

    async function attempt(step) {
      const result = await dispatch(getMe());
      if (cancelled || getMe.fulfilled.match(result)) return;
      if (result.payload?.isAuthError) return; // haqiqiy 401 — reducer allaqachon initialized=true qildi
      if (step >= RETRY_DELAYS_MS.length) {
        // Uzoq muddat tarmoq tiklanmadi — abadiy spinnerda ushlab turmaymiz.
        dispatch(setInitialized());
        return;
      }
      setTimeout(() => { if (!cancelled) attempt(step + 1); }, RETRY_DELAYS_MS[step]);
    }
    attempt(0);
    return () => { cancelled = true; };
  }, [dispatch]);

  // Apply the org's brand color (and reset to default on logout).
  useEffect(() => {
    const normalized = brandColor ? (brandColor.startsWith('#') ? brandColor : '#' + brandColor) : null;
    applyTheme(normalized, brandSolid);
  }, [brandColor, brandSolid]);

  // Bezakli fon (bloom) — sidebar/topbar'dan tashqari, ish maydonining (har
  // qanday sahifadagi <main>) ENG TEPASIGA juda mayin, tez so'nadigan gradient
  // qo'shadi (index.css'dagi body.brand-bloom main). Barcha sahifalarga
  // umumiy Layout/Router bo'lmagani uchun (har sahifa o'z Sidebar/TopBar/main
  // shell'ini o'zi chizadi — App.js'da bitta joy yo'q), shu yerda <body>ga
  // bitta klass qo'yib, CSS orqali GLOBAL qo'llanadi — har bir sahifa
  // faylini alohida o'zgartirish shart emas.
  useEffect(() => {
    document.body.classList.toggle('brand-bloom', !!brandBloom);
    return () => document.body.classList.remove('brand-bloom');
  }, [brandBloom]);

  // Tashkilot o'z logotipini yuklagan bo'lsa — favicon, apple-touch-icon va PWA
  // manifest ("Bosh ekranga qo'shish" ikonkasi) shu logo bilan almashtiriladi.
  // Logo bo'lmasa (yoki chiqish qilinsa) — standart ERP Line ikonkalari qaytariladi.
  useEffect(() => {
    if (savedIconsRef.current === null) {
      savedIconsRef.current = {
        icons: Array.from(document.querySelectorAll('link[rel="icon"], link[rel="alternate icon"]')),
        apple: Array.from(document.querySelectorAll('link[rel="apple-touch-icon"]')),
        manifestHref: document.querySelector('link[rel="manifest"]')?.getAttribute('href') || '/manifest.json',
      };
    }
    const saved = savedIconsRef.current;
    const manifestLink = document.querySelector('link[rel="manifest"]');
    let customIcon = document.getElementById('org-favicon');
    let customApple = document.getElementById('org-apple-icon');

    if (orgLogo) {
      saved.icons.forEach(el => el.remove());
      saved.apple.forEach(el => el.remove());
      const logoUrl = mediaUrl(orgLogo);
      if (!customIcon) {
        customIcon = document.createElement('link');
        customIcon.id = 'org-favicon';
        customIcon.rel = 'icon';
        document.head.appendChild(customIcon);
      }
      customIcon.href = logoUrl;
      if (!customApple) {
        customApple = document.createElement('link');
        customApple.id = 'org-apple-icon';
        customApple.rel = 'apple-touch-icon';
        document.head.appendChild(customApple);
      }
      customApple.href = logoUrl;
      if (manifestLink && orgId) manifestLink.setAttribute('href', `${API}/manifest/${orgId}`);
    } else {
      customIcon?.remove();
      customApple?.remove();
      saved.icons.forEach(el => { if (!el.isConnected) document.head.appendChild(el); });
      saved.apple.forEach(el => { if (!el.isConnected) document.head.appendChild(el); });
      if (manifestLink) manifestLink.setAttribute('href', saved.manifestHref);
    }
  }, [orgLogo, orgId]);

  return (
    <BrowserRouter>
      {/* Line Support "Yordam" tugmasi: faqat Integratsiyalarida 'support' bor tashkilotlarda ishlaydi. */}
      <SupportWidget />
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      }>
      <Routes>
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        {/* Ochiq ro'yxatdan o'tish o'chirilgan (endi faqat Superadmin orqali) — eski havola/bookmark bo'yicha kirgan bo'lsa asosiy sahifaga yuboriladi. */}
        <Route path="/register" element={<Navigate to="/" replace />} />
        {/* Superadmin — tashkilotlarga bog'liq emas, o'z alohida kirishi va tokeni bilan. */}
        <Route path="/superadmin/login" element={<SuperAdminLoginPage />} />
        <Route path="/superadmin" element={<SuperAdminDashboard />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/snab" element={<PrivateRoute><SnabjenistPage /></PrivateRoute>} />
        <Route path="/process" element={<PrivateRoute><ProcessWorkerPage /></PrivateRoute>} />
        <Route path="/sales-agent" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/sales-agent/counterparties" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/sales-agent/map" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/sales-agent/history" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/sales-agent/due-dates" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/sales-agent/balance" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/sales-agent/debtors" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/sales-agent/profile" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/sales-agent/visits" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/sales-agent/reservations" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/sales-agent/collection-kpi" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/sales-agent/kpi" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/sales-agent/activity" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/sales-agent/security" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/sales-agent/agent-transfers" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/sales-agent/sales/:cpId" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/sales-agent/sales/:cpId/history" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/sales-agent/sales/:cpId/payments" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/sales-agent/sales/:cpId/akt-sverka" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/sales-agent/invoice/:id" element={<PrivateRoute><SalesAgentPage /></PrivateRoute>} />
        <Route path="/warehouse-manager" element={<PrivateRoute><WarehouseManagerPage /></PrivateRoute>} />
        <Route path="/warehouse-manager/write-off" element={<PrivateRoute><WarehouseManagerPage /></PrivateRoute>} />
        <Route path="/warehouse-manager/purchases" element={<PrivateRoute><WarehouseManagerPage /></PrivateRoute>} />
        <Route path="/warehouse-manager/inventarizatsiya" element={<PrivateRoute><WarehouseManagerPage /></PrivateRoute>} />
        <Route path="/warehouse-manager/qoldiq" element={<PrivateRoute><WarehouseManagerPage /></PrivateRoute>} />
        <Route path="/warehouse-manager/kochirish" element={<PrivateRoute><WarehouseManagerPage /></PrivateRoute>} />
        <Route path="/warehouse-manager/vozvrat" element={<PrivateRoute><WarehouseManagerPage /></PrivateRoute>} />
        <Route path="/warehouse-manager/invoice/:id" element={<PrivateRoute><WarehouseManagerPage /></PrivateRoute>} />
        <Route path="/warehouse-manager/purchase/:id" element={<PrivateRoute><WarehouseManagerPage /></PrivateRoute>} />
        <Route path="/warehouse-manager/return/:id" element={<PrivateRoute><WarehouseManagerPage /></PrivateRoute>} />
        <Route path="/warehouse-manager/transfer/:id" element={<PrivateRoute><WarehouseManagerPage /></PrivateRoute>} />
        <Route path="/courier" element={<PrivateRoute><CourierPage /></PrivateRoute>} />
        <Route path="/courier/balance" element={<PrivateRoute><CourierPage /></PrivateRoute>} />
        <Route path="/courier/invoice/:id" element={<PrivateRoute><CourierPage /></PrivateRoute>} />
        <Route path="/courier/shipments" element={<PrivateRoute><CourierPage /></PrivateRoute>} />
        <Route path="/debt-collector" element={<PrivateRoute><DebtCollectorPage /></PrivateRoute>} />
        <Route path="/dashboard" element={<Navigate to="/m/dashboard" replace />} />
        <Route path="/m/:key" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
        <Route path="/settings"        element={<AdminRoute><SettingsPage /></AdminRoute>} />
        <Route path="/settings/users/:id" element={<AdminRoute><UserProfilePage /></AdminRoute>} />
        <Route path="/branches/:id/report" element={<PrivateRoute><RequireModule module="sklad-branches"><BranchReportPage /></RequireModule></PrivateRoute>} />
        {/* Har bir bo'lim o'z URL'iga ega: /settings/users, /settings/roles, ... */}
        <Route path="/settings/:tab"   element={<AdminRoute><SettingsPage /></AdminRoute>} />
        <Route path="/products/new"    element={<PrivateRoute><RequireModule module="inventory"><ProductFormPage /></RequireModule></PrivateRoute>} />
        <Route path="/products/:id/edit"  element={<PrivateRoute><RequireModule module="inventory"><ProductFormPage /></RequireModule></PrivateRoute>} />
        <Route path="/products/:id/stock" element={<PrivateRoute><RequireModule module="inventory"><ProductStockCardPage /></RequireModule></PrivateRoute>} />
        <Route path="/purchases/new"       element={<PrivateRoute><RequireModule module="purchases"><PurchaseFormRoute /></RequireModule></PrivateRoute>} />
        <Route path="/purchases/:id/edit"  element={<PrivateRoute><RequireModule module="purchases"><PurchaseFormRoute /></RequireModule></PrivateRoute>} />
        <Route path="/sales/new"           element={<PrivateRoute><RequireModule module="sales"><SalesFormPage /></RequireModule></PrivateRoute>} />
        <Route path="/sales/:id/edit"      element={<PrivateRoute><RequireModule module="sales"><SalesFormPage /></RequireModule></PrivateRoute>} />
        <Route path="/sales/analytics"     element={<PrivateRoute><RequireModule module="sales" hiddenDataKey="sales.analytics"><SalesAnalyticsPage /></RequireModule></PrivateRoute>} />
        <Route path="/sales/analytics/agent/:userId" element={<AdminRoute><RequireModule module="sales" hiddenDataKey="sales.analytics"><SalesAgentDetailPage /></RequireModule></AdminRoute>} />
        <Route path="/my-tasks" element={<PrivateRoute><RequireModule module="tasks"><MyTasksPage /></RequireModule></PrivateRoute>} />
        <Route path="/my-tasks/task/:taskId" element={<PrivateRoute><RequireModule module="tasks"><MyTaskDetailPage /></RequireModule></PrivateRoute>} />
        <Route path="/my-tasks/:boardId" element={<PrivateRoute><RequireModule module="tasks"><TaskBoardPage simple /></RequireModule></PrivateRoute>} />
        <Route path="/tasks/:boardId" element={<PrivateRoute><RequireModule module="tasks"><TaskBoardPage /></RequireModule></PrivateRoute>} />
        <Route path="/visits/agent/:userId" element={<PrivateRoute><RequireModule module="visits"><VisitsAgentDetailPage /></RequireModule></PrivateRoute>} />
        <Route path="/sales/analytics/:section" element={<PrivateRoute><RequireModule module="sales" hiddenDataKey="sales.analytics"><SalesAnalyticsPage /></RequireModule></PrivateRoute>} />
<Route path="/payments/new"        element={<PrivateRoute><RequireModule module="finance-payments"><PaymentFormPage /></RequireModule></PrivateRoute>} />
        <Route path="/payments/:id/edit"   element={<PrivateRoute><RequireModule module="finance-payments"><PaymentFormPage /></RequireModule></PrivateRoute>} />
        <Route path="/counterparties/agent-matrix" element={<AdminRoute><RequireModule module="counterparties"><CounterpartyAgentMatrixPage /></RequireModule></AdminRoute>} />
        <Route path="/counterparties/:id"  element={<PrivateRoute><RequireModule module="counterparties"><CounterpartyCardPage /></RequireModule></PrivateRoute>} />
        <Route path="/cashboxes/:id"       element={<PrivateRoute><RequireModule module="finance-cashboxes"><CashboxCardPage /></RequireModule></PrivateRoute>} />
        <Route path="/sales-points/:id"    element={<PrivateRoute><SalesPointCardPage /></PrivateRoute>} />
        <Route path="/transfers/new"       element={<PrivateRoute><RequireModule module="sklad-transfers"><TransferFormPage /></RequireModule></PrivateRoute>} />
        <Route path="/transfers/:id/edit"  element={<PrivateRoute><RequireModule module="sklad-transfers"><TransferFormPage /></RequireModule></PrivateRoute>} />
        <Route path="/inventarizatsiya/new"      element={<PrivateRoute><RequireModule module="sklad-inventarizatsiya"><InventarizatsiyaFormPage /></RequireModule></PrivateRoute>} />
        <Route path="/inventarizatsiya/:id/edit" element={<PrivateRoute><RequireModule module="sklad-inventarizatsiya"><InventarizatsiyaFormPage /></RequireModule></PrivateRoute>} />
        <Route path="/write-offs/new"      element={<PrivateRoute><RequireModule module="sklad-writeoff"><WriteOffFormPage /></RequireModule></PrivateRoute>} />
        <Route path="/write-offs/:id/edit" element={<PrivateRoute><RequireModule module="sklad-writeoff"><WriteOffFormPage /></RequireModule></PrivateRoute>} />
        <Route path="/production/analytics" element={<PrivateRoute><RequireModule module="production"><ProductionAnalyticsPage /></RequireModule></PrivateRoute>} />
        <Route path="/production/new"       element={<PrivateRoute><RequireModule module="production"><ProductionFormPage /></RequireModule></PrivateRoute>} />
        <Route path="/production/:id/edit"  element={<PrivateRoute><RequireModule module="production"><ProductionFormPage /></RequireModule></PrivateRoute>} />
        <Route path="/job-orders/tv"          element={<PrivateRoute><RequireModule module="job-orders"><JobOrdersTVPage /></RequireModule></PrivateRoute>} />
        <Route path="/job-orders/map"         element={<PrivateRoute><RequireModule module="job-orders"><JobOrdersMapPage /></RequireModule></PrivateRoute>} />
        <Route path="/job-orders/new"        element={<PrivateRoute><RequireModule module="job-orders"><JobOrderFormPage /></RequireModule></PrivateRoute>} />
        <Route path="/job-orders/:id/edit"   element={<PrivateRoute><RequireModule module="job-orders"><JobOrderFormPage /></RequireModule></PrivateRoute>} />
        <Route path="/job-orders/:jobOrderId/smeta" element={<PrivateRoute><RequireModule module="job-orders"><SmetaPage /></RequireModule></PrivateRoute>} />
        <Route path="/procurement/new"       element={<PrivateRoute><RequireModule module="purchases-procurement"><ProcurementFormPage /></RequireModule></PrivateRoute>} />
        <Route path="/procurement/:id/edit"  element={<PrivateRoute><RequireModule module="purchases-procurement"><ProcurementFormPage /></RequireModule></PrivateRoute>} />
        {/* Akt-sverka public — autentifikatsiyasiz, token bilan ochiq */}
        <Route path="/akt-sverka/:token"     element={<AktSverkaPublicPage />} />
        <Route path="/price-list/:token"     element={<PriceListPublicPage />} />
        <Route path="/smeta/:token"           element={<SmetaPublicPage />} />
        {/* Sotuv fakturasi public — autentifikatsiyasiz, token bilan ochiq (QR-kod uchun) */}
        <Route path="/sales-invoice/:token"   element={<SalesInvoicePublicPage />} />
        <Route path="/" element={<RootRedirect />} />
      </Routes>
      </Suspense>
      {/* Ekran qulflash overlay — autentifikatsiyalangan foydalanuvchi uchun
          har joyda ko'rinishi mumkin (Redux 'locked' state'idan o'qiydi) */}
      <LockScreen />
      <NotificationToastContainer />
      <PushInit />
      <CourierTrackingInit />
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <Provider store={store}>
      <Toaster position="top-center" />
      <OfflineBanner />
      <UpdatePrompt />
      <AppInit />
    </Provider>
  );
}
